import { Button } from '@factorialco/deprecated-design-system'
import type { Form } from 'factorial-form'
import { UpsertCustomValueDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { InputItem } from 'modules/custom_resources/components/CustomFields/CustomFieldsForm/InputItem'

// FIXME: this should be exposed by the GraphQL API
export type ValuableType = 'Employee' | 'Document'

interface Props {
  valuableId: number
  valuableType: ValuableType
  readonly?: boolean
  editableFieldsOnly?: boolean
  forms: Form[]
  isVisible: boolean
  currency?: string
}

const FieldsForm = ({
  valuableId,
  valuableType,
  readonly,
  editableFieldsOnly,
  forms,
  isVisible,
  currency,
}: Props) => {
  const [upsertCustomValue] = useMutation(UpsertCustomValueDocument, {
    extractorFn: (data) => data.customFieldsMutations.upsertCustomfieldsValue,
  })

  if (!isVisible) return null

  const running = false
  const onSubmit = async () => {
    // NOTE: I expected factorial-form to clean errors automatically but apparently it doesn't
    const editedForms = forms.filter((form) => form.get('editable').value && form.isDirty)
    editedForms.forEach((form) => form.setErrors({}))

    const results = await Promise.all(
      editedForms
        .map((form) => form.data())
        .map((formData) => {
          // TODO: consider if we need the id or not because fieldId, valuableId, valuableType are already a kind of id
          const id = parseInt(formData.value_id, 10)
          const value = formData.value?.toString() ?? ''

          if (id) {
            return upsertCustomValue({
              variables: {
                id,
                value,
              },
            })
          }
          return upsertCustomValue({
            variables: {
              value,
              fieldId: parseInt(formData.field_id, 10),
              valuableId,
              valuableType,
            },
          })
        })
    )

    const handledResults = results.map((result, index) =>
      handleResult(result, (errors) => {
        // NOTE: errors is a string because it was meant to be used by Formik forms.
        //       We are transforming the object so it is factorial-form compatible.
        editedForms[index]?.setErrors(
          Object.fromEntries(Object.entries(errors).map(([key, value]) => [key, value.split(', ')]))
        )
      })
    )

    if (handledResults.every((handled) => handled.success)) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.save_error'),
      })
    }
  }

  const someFormsAreEditable = forms.some((form) => form.get('editable').value)
  const someFormsAreDirty = forms.some((form) => form.isDirty)
  const showSubmit = !running && !readonly && someFormsAreEditable && someFormsAreDirty

  return (
    <>
      {forms
        .filter((form) => (editableFieldsOnly ? form.get('editable').value : true))
        .map((form: Form, key: number) => {
          const optional = {
            readonly,
          }

          return (
            <FormRow key={key}>
              <InputItem form={form} optional={optional} currency={currency} />
            </FormRow>
          )
        })}
      {showSubmit && (
        <FormRow footer>
          <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
        </FormRow>
      )}
    </>
  )
}

export default observer(FieldsForm)
